import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanExtendVideoParams {
  /**
   * 作业ID，必填
   */
  jobId: string;
  /**
   * 视频编号，必填，范围0-3
   * @values 0 | 1 | 2 | 3
   */
  videoNo: 0 | 1 | 2 | 3;
  /**
   * 文本提示，必填，长度必须在1-8192个字符之间
   */
  text: string;
  /**
   * 文本提示（别名），必填，长度必须在1-8192个字符之间
   */
  prompt?: string;
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback?: string;
}

export interface YouchuanExtendVideoResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_extend_video
 *
 * 通过悠船视频扩展API创建视频扩展任务。
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E8%A7%86%E9%A2%91%E7%94%9F%E6%88%90/api_videoextend
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanExtendVideo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanExtendVideo({
 *   jobId: 'your-job-id',
 *   videoNo: 0,
 *   text: 'Extend the video with beautiful scenery'
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanExtendVideoParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanExtendVideo(
  params: YouchuanExtendVideoParams,
): Promise<YouchuanExtendVideoResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_extend_video',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanExtendVideoResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
